import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import api from 'helpers/api'
import { useTranslation } from 'react-i18next'
import Collapsible from 'components/common/Collapsible/Collapsible'
import Button from 'components/common/Button/Button'
import Link from 'components/common/Link/Link'
import LogoShort from 'components/assets/icons/LogoShort'
import Title from 'components/common/PageTitle/PageTitle'
import { pages } from 'components/Freemium/Nav/Nav'
import classNames from 'classnames'
import style from './FreemiumMain.module.scss'
import mainStyle from '../FreemiumComponents.module.scss'
import RoundArrow from 'components/assets/icons/RoundArrow'
import { parseToken } from 'helpers/auth'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

const FreemiumMain = () => {
  const { state } = useLocation()
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [contract, setContract] = useState()
  const token = state?.token || searchParams.get('token')
  const idCampaign = state?.idCampaign || searchParams.get('idCampaign')
  const {adhesionId, redirectTo} = parseToken(token) || {}
  const contractTypeId = params.id
  const { setPartnerLogo } = useOutletContext()

  // Get datas
  useEffect(() => {
    const getDatas = async () => {
      try{
        // Get contractType details
        const contractType = await api.get({
          url: `/owen-products/contract_type/${contractTypeId}/client`
        })
        // Get wordings
        const wordings = await api.get({
          url: `/contract-type/${contractTypeId}/wordings`,
          headers: { Authorization: `Bearer ${token}` }
        })
        setContract({
          contractType: [{...contractType, wordings}]
        })
        setPartnerLogo(wordings?.logo)
        // Get adhesions details
        if (adhesionId){
          const adhesion = await api.get({
            url: `/adhesions/${adhesionId}/subscription`,
            headers: { Authorization: `Bearer ${token}` }
          })
          const phoneDatas = parsePhoneNumberFromString(adhesion.phone)
          adhesion.phone = {
            number: adhesion.phone.replace('+', ''),
            countryCode: phoneDatas ? phoneDatas.country.toLowerCase() : 'fr'
          }
          const {
            gender, firstname, lastname, birthdate, placeOfBirth, countryOfBirth,
            address, zipcode, city, country, additionalAddress,
            email, phone,
            dependent,
            cancelationHorodate
          } = adhesion

          navigate('./'+redirectTo, {
            replace: true,
            state: {
              client: {
                firstname,
                lastname,
                placeOfBirth,
                countryOfBirth,
                gender,
                address,
                zipcode,
                city,
                country,
                additionalAddress,
                email,
                genderPartner: dependent?.gender,
                firstnamePartner: dependent?.firstname,
                lastnamePartner: dependent?.lastname,
                placeOfBirthPartner: dependent?.placeOfBirth,
                countryOfBirthPartner: dependent?.countryOfBirth,
                birthdate: birthdate.split('T')[0].split('-').reverse().join('/'),
                birthdatePartner: dependent && dependent.birthdate.split('T')[0].split('-').reverse().join('/'),
                phone,
                partnerOption: !!dependent
              },
              contract: {
                contractType: [{...contractType, wordings}]
              },
              token,
              endFree: cancelationHorodate
            }
          })
        }
      } catch(err) {
        console.log('get contract issue', err.message)
      }
    }
    getDatas()
  }, [navigate, adhesionId, redirectTo, contractTypeId, token, setPartnerLogo])


  // Next page
  const onSubmit = () => {
    navigate(pages[1], {
      state: {
        client: state?.client,
        contract: state?.contract || contract,
        token,
        idCampaign,
        contractPay: searchParams.get('contractPay')
      },
    })
  }

  const {
    subTitlePromotion,
    titlePromotion,
    description,
    coveredEvents,
    indemnities
  } = contract?.contractType[0]?.wordings?.home || {}

  return contract ? (
    <>
      <Title className={mainStyle['page-title']} text={<>{t('freemium:start.title')} 🎉</>} />

      <div className={style.info}>
        <ul>
          <li><span><RoundArrow /></span>{t('freemium:start.catchLine1')}</li>
          <li><span><RoundArrow /></span>{t('freemium:start.catchLine2')}</li>
        </ul>
        <p>{t('freemium:start.solution')}</p>
      </div>

      <div className={style.cards}>

        <div className={style.price}>
          <span className={style['price-description']}>
            {subTitlePromotion}
          </span>

          <h3 className={classNames(mainStyle.shining_title, mainStyle.max)}>
            {titlePromotion}
          </h3>
        </div>


        <div className={style.product}>
          <h4 className={style['product-title']}>
            <LogoShort className={style.logo} /> {t('common:yourProtection')}
          </h4>

          <p className={style['product-description']}>
            {description}
          </p>
        </div>


        <Collapsible
          className={style['collapsible-card']}
          title={<span className={style['list-title']}>⚠️ {t('freemium:start.eventsCovered')}</span>}
          isOpenedProp={true}
          content={
            <ul className={style.list}>
              {(coveredEvents || '').split('//').map((item, index) => (
                <li className={style.item} key={`covered-event-${index}`}>
                  {item}
                </li>
              ))}
            </ul>
          }
        />


        <Collapsible
          className={style['collapsible-card']}
          title={<span className={style['list-title']}>💸 {t('freemium:start.indemnity')}</span>}
          isOpenedProp={true}
          content={
            <ul className={style.list}>
              {(indemnities || '').split('//').map((item, index) => (
                <li className={style.item} key={`indemnity-${index}`}>
                  {item}
                </li>
              ))}
            </ul>
          }
        />
      </div>


      <Button
        className={classNames(mainStyle.button, style.button)}
        label={t('freemium:start.button')}
        onClick={onSubmit}
        type="primary"
      />


      <p className={style.link}>
        {`${t('common:consultThe')} `}
        <Link
          label={t('contract:document.ipid').toLowerCase()}
          target="_blank"
          to={contract.contractType[0].legalDocuments.ni}
        />
        {` ${t('common:misc.andThe')} `}
        <Link
          label={t('contract:document.niShort').toLowerCase()}
          target="_blank"
          to={contract.contractType[0].legalDocuments.ipid}
        />
        {` ${t('common:misc.onThe')} ${t('common:insuranceProduct')}`}
      </p>
    </>
  ) : null
}

export default FreemiumMain